import { DatePipe } from '@angular/common';
import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { RegisterData, RegisterParams } from '../resolvers/register.resolver';
import { getLocaleDateString, toUniversalDateString } from '../utils/date-utils';
import { getCurrentRegisterLocale, TypedActivatedRoute } from '../utils/route-utils';

@Pipe({
  name: 'aadaterange',
  standalone: false,
})
export class AaDateRangePipe implements PipeTransform {
  private datePipe = new DatePipe(this.locale);

  constructor(
    @Inject(LOCALE_ID) private locale: string,
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
  ) {}

  transform(
    value: string | [string, string] | undefined | null,
    endDate?: string | null,
    lineBreak = false,
  ): any {
    if (!value) {
      return null;
    }

    let dates: [string, string];
    if (typeof value === 'string') {
      if (!endDate) {
        return null;
      }
      dates = [value, endDate];
    } else {
      dates = value;
    }

    const from = toUniversalDateString(dates[0]);
    const to = toUniversalDateString(dates[1]);

    if (!from || !to) {
      return null;
    }

    const locale = getCurrentRegisterLocale(this.route.snapshot);
    const format = getLocaleDateString(locale);

    if (from === to) {
      switch (format) {
        case 'MDY':
          return this.datePipe.transform(from, 'MMM d yyyy');

        case 'YMD':
          return this.datePipe.transform(from, 'yyyy MMM d');

        // default is 'DMY'
        default:
          return this.datePipe.transform(from, 'd MMM yyyy');
      }
    }

    if (from.substring(0, 5) === to.substring(0, 5)) {
      if (from.substring(0, 8) === to.substring(0, 8)) {
        // <= calendar month
        switch (format) {
          case 'MDY':
            return [
              this.datePipe.transform(from, 'MMM d'),
              this.datePipe.transform(to, 'd yyyy'),
            ].join('‑');

          case 'YMD':
            return [
              this.datePipe.transform(from, 'yyyy MMM d'),
              this.datePipe.transform(to, 'd'),
            ].join('‑');

          // default is 'DMY'
          default:
            return [
              this.datePipe.transform(from, 'd'),
              this.datePipe.transform(to, 'd MMM yyyy'),
            ].join('‑');
        }
      }

      // <= calendar year
      switch (format) {
        case 'MDY':
          return [
            this.datePipe.transform(from, 'MMM d'),
            this.datePipe.transform(to, 'MMM d yyyy'),
          ].join(lineBreak ? ' \n ' : ' ‑ ');

        case 'YMD':
          return [
            this.datePipe.transform(from, 'yyyy MMM d'),
            this.datePipe.transform(to, 'MMM d'),
          ].join(lineBreak ? ' \n ' : ' ‑ ');

        // default is 'DMY'
        default:
          return [
            this.datePipe.transform(from, 'd MMM'),
            this.datePipe.transform(to, 'd MMM yyyy'),
          ].join(lineBreak ? ' \n ' : ' ‑ ');
      }
    }

    // >= calendar year
    switch (format) {
      case 'MDY':
        return [
          this.datePipe.transform(from, 'MMM d yyy'),
          this.datePipe.transform(to, 'MMM d yyyy'),
        ].join(lineBreak ? ' \n ' : ' ‑ ');

      case 'YMD':
        return [
          this.datePipe.transform(from, 'yyyy MMM d'),
          this.datePipe.transform(to, 'yyyy MMM d'),
        ].join(lineBreak ? ' \n ' : ' ‑ ');

      // default is 'DMY'
      default:
        return [
          this.datePipe.transform(from, 'd MMM yyyy'),
          this.datePipe.transform(to, 'd MMM yyyy'),
        ].join(lineBreak ? ' \n ' : ' ‑ ');
    }
  }
}
